import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Heart, ArrowRight, Loader2, Plus, X, CheckCircle2, CalendarClock, ShieldCheck, FileText,
} from "lucide-react";

const COMM_PREFS = [
  "I love to text",
  "I prefer email",
  "I prefer video calls",
  "I love meeting in person",
  "Phone calls work best",
];

const MentorSignup = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    field_of_expertise: "",
    years_experience: "",
    current_role: "",
    communication_preference: "",
    personal_interests: "",
    availability: "",
    linkedin_url: "",
    city: "",
    state: "",
    why_mentor: "",
  });
  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addInterest = () => {
    const v = interestInput.trim();
    if (v && !interests.includes(v)) {
      setInterests([...interests, v]);
      setInterestInput("");
    }
  };
  const removeInterest = (x) => setInterests(interests.filter((i) => i !== x));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      setError("Please provide your name, email, and a password of at least 6 characters.");
      return;
    }
    if (!form.field_of_expertise.trim()) {
      setError("Please tell us your field of expertise.");
      return;
    }
    if (form.why_mentor.trim().length < 20) {
      setError("Please tell us a bit more about why you'd like to mentor (min 20 characters).");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/mentor/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, areas_of_interest: interests }),
      });
      let data = {};
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.detail || `Signup failed (HTTP ${res.status})`);
      if (data.user) setUser(data.user);
      toast.success("Application submitted! Our team will review it and be in touch soon.");
      navigate("/dashboard?onboarding=mentor");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="mentor-signup-page">
      <Header />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-14 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Become a Mentor</p>
            <h1 className="text-3xl sm:text-5xl font-heading font-bold leading-tight">
              Share your experience. Change a career.
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Create your mentor profile below. It helps us match you with the right mentees and gives
              them a sense of who you are. Applications are reviewed by our team before you go live.
            </p>
            <Link
              to="/mentor-guide"
              className="inline-flex items-center gap-1 text-sm text-[#FFD507] hover:underline mt-4 font-semibold"
              data-testid="mentor-signup-guide-link"
            >
              <FileText className="w-4 h-4" /> Read: How to be a great mentor & what's expected
            </Link>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_320px] gap-8">
          <Card className="border border-slate-200">
            <CardContent className="p-6 sm:p-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4" data-testid="mentor-signup-error">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ms-name">Full name *</Label>
                    <Input id="ms-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="ms-name" />
                  </div>
                  <div>
                    <Label htmlFor="ms-email">Email *</Label>
                    <Input id="ms-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="ms-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ms-password">Choose a password *</Label>
                    <Input id="ms-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} minLength={6} required data-testid="ms-password" />
                    <p className="text-xs text-slate-500 mt-1">You'll use this to sign in and manage your mentor profile.</p>
                  </div>
                  <div>
                    <Label htmlFor="ms-field">Field of expertise *</Label>
                    <Input id="ms-field" value={form.field_of_expertise} onChange={(e) => set("field_of_expertise", e.target.value)} placeholder="e.g. QA Engineering, Data Analytics, UX Design" required data-testid="ms-field" />
                  </div>
                  <div>
                    <Label htmlFor="ms-years">Years of experience</Label>
                    <Input id="ms-years" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} placeholder="e.g. 8 years" data-testid="ms-years" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="ms-role">Current role &amp; company</Label>
                    <Input id="ms-role" value={form.current_role} onChange={(e) => set("current_role", e.target.value)} placeholder="e.g. Senior QA Analyst at Acme Corp" data-testid="ms-role" />
                  </div>
                </div>

                <div>
                  <Label>Areas you can help mentees with</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={interestInput}
                      onChange={(e) => setInterestInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addInterest())}
                      placeholder="Add a topic and press Enter (e.g. resume review, interview prep)"
                      data-testid="ms-interest-input"
                    />
                    <Button type="button" onClick={addInterest} variant="outline" data-testid="ms-interest-add"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {interests.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {interests.map((i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-[#FFD507]/15 text-[#1E3A8A] rounded-full text-sm" data-testid={`ms-interest-${i}`}>
                          {i}
                          <button type="button" onClick={() => removeInterest(i)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>How do you prefer to communicate?</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="ms-comm-prefs">
                    {COMM_PREFS.map((c) => {
                      const active = form.communication_preference === c;
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => set("communication_preference", active ? "" : c)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            active
                              ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                              : "bg-white text-slate-700 border-slate-300 hover:border-[#1E3A8A]"
                          }`}
                          data-testid={`ms-comm-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="ms-personal">Personal interests &amp; hobbies</Label>
                  <Input id="ms-personal" value={form.personal_interests} onChange={(e) => set("personal_interests", e.target.value)} placeholder="e.g. hiking, board games, sci-fi novels — helps mentees connect with you" data-testid="ms-personal" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ms-availability">Availability</Label>
                    <Input id="ms-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="e.g. Evenings & weekends, 2 hrs/month" data-testid="ms-availability" />
                  </div>
                  <div>
                    <Label htmlFor="ms-linkedin">LinkedIn URL</Label>
                    <Input id="ms-linkedin" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="ms-linkedin" />
                  </div>
                  <div>
                    <Label htmlFor="ms-city">City</Label>
                    <Input id="ms-city" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="ms-city" />
                  </div>
                  <div>
                    <Label htmlFor="ms-state">State</Label>
                    <Input id="ms-state" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="IL" data-testid="ms-state" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="ms-why">Why do you want to mentor? *</Label>
                  <Textarea id="ms-why" rows={4} value={form.why_mentor} onChange={(e) => set("why_mentor", e.target.value)} minLength={20} placeholder="Tell us what draws you to mentoring neurodivergent professionals…" required data-testid="ms-why" />
                  <p className="text-xs text-slate-400 mt-1">{form.why_mentor.trim().length}/20 minimum</p>
                </div>

                <Button type="submit" disabled={loading} className="btn-primary w-full h-11" data-testid="ms-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Submit mentor application <ArrowRight className="w-4 h-4 ml-1" /></>)}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#1E3A8A] hover:underline" data-testid="ms-existing-link">Sign in instead</Link>
                </p>
              </form>
            </CardContent>
          </Card>

          <aside className="space-y-4">
            <Card className="border border-[#FFD507]/40 bg-[#FFD507]/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-[#0F1B3D] font-heading font-bold">
                  <Heart className="w-5 h-5" /> The mentor commitment
                </div>
                <ul className="text-sm text-slate-700 mt-4 space-y-2">
                  <li className="flex gap-2"><CalendarClock className="w-4 h-4 text-[#1E3A8A] mt-0.5" /> Meet your mentee at least once per month</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> A one-year minimum commitment</li>
                  <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-[#1E3A8A] mt-0.5" /> Reasonable response times &amp; professionalism</li>
                </ul>
                <Link to="/mentor-guide" className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline mt-4 font-semibold" data-testid="mentor-signup-guide-link-2">
                  Read the full mentor guide <ArrowRight className="w-3 h-3" />
                </Link>
              </CardContent>
            </Card>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MentorSignup;
